import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Textarea } from '@/components/ui/text-area.tsx'

export const MAX_NOTE_LENGTH = 500

interface NoteFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  disabled?: boolean
  errors?: FieldErrors<T>
  noteValue?: string
  isOptional?: boolean
  maxNoteLength?: number
}

export const NoteField = <T extends FieldValues>({
  control,
  name,
  disabled = false,
  errors,
  noteValue = '',
  isOptional = false,
  maxNoteLength = MAX_NOTE_LENGTH,
}: NoteFieldProps<T>) => {
  const { t } = useTranslation()

  return (
    <div className="space-y-1">
      <label htmlFor="note" className="text-sm font-medium">
        {t('deliverableSet.dialog.fields.note')}
        {isOptional && (
          <span className="text-sm text-quaternary-500 font-light">
            {' '}
            (Optional)
          </span>
        )}
      </label>

      <Controller
        name={name}
        control={control}
        rules={{
          maxLength: {
            value: MAX_NOTE_LENGTH,
            message: t('deliverableSet.dialog.validation.maxLengthExceeded', {
              max: MAX_NOTE_LENGTH,
            }),
          },
        }}
        render={({ field }) => (
          <Textarea
            id="note"
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t('deliverableSet.dialog.placeholders.enterNote')}
            disabled={disabled}
            maxLength={maxNoteLength}
            className="resize-y"
          />
        )}
      />

      <div className="flex justify-end text-xs text-gray-500">
        {noteValue?.length ?? 0}/{maxNoteLength}
      </div>

      {errors?.[name] && (
        <p className="mt-1 text-sm text-red-500">
          {errors[name]?.message as string}
        </p>
      )}
    </div>
  )
}
